/*
 * Grouping the notifications feed by day, and choosing an alert's destination.
 *
 * Pure functions over the payload, split out from the screen for the reason
 * every other lib/ helper is: this is where the awkward decisions live, and they
 * are testable without a server or a DOM.
 *
 * WHICH DAY, AND WHY IT IS THE DEVICE'S. Build rule 7 says venue-local times
 * always, and this is the one place in the app that deliberately does not follow
 * it — stated here rather than discovered later. That rule is about EVENT times:
 * a bout starts when the venue's clock says so, and a coach in Chicago reading
 * about an Eastern tournament needs the venue's 9:00, not their own 8:00. An
 * alert's `observed_at` is not an event time. It is the moment the app noticed
 * something, placed on the READER's timeline, and "Today" in a notifications
 * feed means the day the reader is having. Grouping an alert about a Portland
 * tournament under Pacific dates would put this morning's alerts under
 * yesterday for a reader in Des Moines, which is the opposite of honest.
 *
 * The alert's CONTENT still renders in venue time — a deadline day through
 * decision 360's rule, a start time through the venue zone — because those are
 * event facts. Only the feed's own day headings are the device's.
 */

import type { Alert } from "./api";
import { deviceZone } from "./format";

/**
 * One day's worth of alerts.
 *
 * `key` is a stable identifier and never a label: direction §7 bars literals in
 * components, so the screen turns `daysAgo` into a translated heading and uses
 * `key` only for React and for tests.
 */
export interface AlertDayGroup {
  /** The device-zone calendar day, as YYYY-MM-DD. */
  key: string;
  /**
   * How many days back from today, in the device zone: 0 is today, 1 yesterday,
   * anything larger is dated.
   *
   * A NUMBER RATHER THAN A LABEL, for the same reason `/api/me` sends a count
   * rather than a boolean: "two days ago" is a fact, and whether that renders as
   * "Today", "Yesterday" or "Mon 1 Sep" is display state the screen owns.
   */
  daysAgo: number;
  alerts: Alert[];
}

/** The device-zone calendar day of an instant, as YYYY-MM-DD. */
function dayKey(instant: string, zone: string): string | null {
  const d = new Date(instant);
  if (Number.isNaN(d.getTime())) return null;

  /*
   * Intl rather than string surgery on the ISO text, which is the trap
   * `venueDateLabel` documents at length: slicing "2026-09-03T02:30:00Z" to its
   * first ten characters gives a UTC day, and for a reader in Chicago that
   * instant is still 2 September. `en-CA` yields YYYY-MM-DD, which sorts.
   */
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/**
 * Group alerts into days, newest first, preserving the server's order within
 * each day.
 *
 * THE SERVER'S ORDER IS NOT RE-SORTED, deliberately. The feed arrives ordered by
 * (observed_at, id) and a client-side sort on `observed_at` alone would scramble
 * exactly the rows the ladder produces most of — decision 408 stamps a whole
 * generator run with one instant, so a stable-looking sort would reorder them
 * arbitrarily and paging would then disagree with what is on screen.
 *
 * `now` is injectable so the day arithmetic can be tested at a fixed instant
 * rather than only on the day the suite happens to run.
 */
export function groupByDay(
  alerts: readonly Alert[],
  now: Date = new Date(),
  zone: string = deviceZone(),
): AlertDayGroup[] {
  const today = new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);

  const groups: AlertDayGroup[] = [];
  const byKey = new Map<string, AlertDayGroup>();

  for (const alert of alerts) {
    const key = dayKey(alert.observed_at, zone);
    // An unparseable instant is dropped rather than grouped under a made-up day.
    // It cannot happen against this server — the field is always RFC 3339 — and
    // inventing a heading for it would be a placeholder, which contract §0 bars.
    if (!key) continue;

    let group = byKey.get(key);
    if (!group) {
      group = { key, daysAgo: daysBetween(key, today), alerts: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.alerts.push(alert);
  }

  return groups;
}

/**
 * Whole days between two YYYY-MM-DD keys.
 *
 * Parsed as UTC midnights and subtracted, which is safe precisely BECAUSE both
 * sides are already calendar days in one zone: the zone conversion happened in
 * `dayKey`, so this is arithmetic over labels rather than over instants, and no
 * daylight-saving transition can shorten one of these "days" to 23 hours.
 */
function daysBetween(key: string, today: string): number {
  const a = Date.parse(`${key}T00:00:00Z`);
  const b = Date.parse(`${today}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86_400_000);
}

/**
 * Where an alert's link goes.
 *
 * EVERY KIND THIS PHASE CAN SHOW LANDS ON UPCOMING, and that is a fact about the
 * inventory rather than a simplification. The planning regime is deadlines,
 * registrations, event times, cancellation and the day-before reminder — all of
 * them about a tournament that has not happened yet, which is Upcoming's whole
 * subject. Active and Past become destinations at stage 4a, when the day-of
 * kinds and placements arrive.
 *
 * THE ANCHOR IS WHAT MAKES IT A REAL DESTINATION rather than a link to a screen.
 * §4.1 asks for "a real link to its destination", and a bare `/upcoming` for a
 * coach with eleven tournaments is a link to a scroll. The fragment names the
 * tournament card's own element id, so the browser's native fragment scrolling
 * does the work — no interception, no scrollIntoView, and the URL is shareable
 * and survives a reload (build rule 10).
 *
 * EVERY KIND RESOLVES A TOURNAMENT, including the four that name only an event:
 * the feed query reaches the tournament through the event rather than the ledger
 * duplicating the reference, so there is one anchor vocabulary and not two. Null
 * is still handled, because contract §0's rule is that a URL in a payload is a
 * promise — an alert with no tournament renders as text with no link rather than
 * as a link that goes nowhere.
 */
export function alertDestination(alert: Alert): string | null {
  const id = alert.tournament?.id ?? null;
  return id ? `/upcoming#tournament-${id}` : null;
}
